const PROB_REPORT_CONTEXT_LIMIT = 14000;

export const PROB_REPORT_BUDDY_INSTRUCTIONS = `あなたはSinkのAI Buddyです。
ユーザーはProbの「パラダイムシフト・レーダー」レポートを文脈にして議論しています。

Probレポートは通常のニュース記事ではありません。
見慣れた技術・制度・社会ニュースを、1-3年の構造的ポジショニングへ翻訳するための分析です。
目的は「何が起きたか」の要約ではなく、「希少性や制約がどこへ移動し、ユーザーの投資・キャリア・事業の構えにどう効くか」を議論することです。

回答では、Probレポートを絶対視せず、仮説として扱ってください。
ユーザーの関心に応じて、レポートの構造の噛み砕き、ユーザーの業界・仕事・投資テーマへの引き寄せ、前提・弱点・反対シナリオ、次に観察すべきシグナルの整理を支援してください。`;

const PROB_SECTION_KEYS = [
  'seed',
  'soil',
  'trigger',
  'bottleneck',
  'yMacro',
  'yMicro',
  'cConstraint',
];

const PROB_SECTION_LABELS = {
  seed: 'seed: 技術・制度・社会変化の種。何が変化の起点か。',
  soil: 'soil: その種が育つための社会的土壌。制度、欲望、資源・インフラの観点。',
  trigger: 'trigger: 現状維持を破り、変化を着火させる触媒。',
  bottleneck: 'bottleneck: 社会の希少性・制約がどこからどこへ移るか。',
  yMacro: 'yMacro: 産業構造、資本配分、市場、企業への影響。',
  yMicro: 'yMicro: 個人の仕事、スキル、生活、意思決定への影響。',
  cConstraint: 'cConstraint: その変化のあとに新しく現れる制約。',
};

export function isValidSharedAssetId(id) {
  return typeof id === 'string' && id.trim().length > 0 && !id.includes('/');
}

export function isProbReportSharedAsset(asset) {
  return Boolean(
    asset &&
    asset.sourceApp === 'prob' &&
    asset.sharedType === 'prob_report' &&
    asset.status === 'published'
  );
}

function stringifyDate(value) {
  if (!value) return '';
  if (typeof value === 'string') return value;
  if (typeof value.toDate === 'function') return value.toDate().toISOString();
  if (value.seconds) return new Date(value.seconds * 1000).toISOString();
  return String(value);
}

function compactList(values, formatter, maxItems = 8) {
  if (!Array.isArray(values) || values.length === 0) return '';
  return values.slice(0, maxItems).map(formatter).filter(Boolean).join('\n');
}

function formatSourceLink(link) {
  if (!link) return '';
  if (typeof link === 'string') return `- ${link}`;
  const label = link.label || link.title || link.url || 'source';
  const type = link.sourceType ? ` (${link.sourceType})` : '';
  return `- ${label}${type}${link.url ? `: ${link.url}` : ''}`;
}

function formatKeyFactor(factor) {
  if (!factor) return '';
  if (typeof factor === 'string') return `- ${factor}`;
  const parts = [];
  if (factor.axis) parts.push(`axis=${factor.axis}`);
  if (factor.assessment) parts.push(`assessment=${factor.assessment}`);
  if (typeof factor.realized === 'boolean') parts.push(`realized=${factor.realized}`);
  const prefix = parts.length > 0 ? `[${parts.join(', ')}] ` : '';
  const evidence = factor.evidence ? `\n  evidence: ${formatSourceLink(factor.evidence).replace(/^- /, '')}` : '';
  return `- ${prefix}${factor.statement || ''}${evidence}`;
}

function formatSection(key, section, options = {}) {
  if (!section) return '';
  const { compact = false } = options;
  const lines = [`## ${key}`, PROB_SECTION_LABELS[key]];
  if (section.sectionTitle) lines.push(`sectionTitle: ${section.sectionTitle}`);
  if (section.summary) lines.push(`summary: ${section.summary}`);
  if (typeof section.confidence === 'number') lines.push(`confidence: ${section.confidence}`);

  if (!compact) {
    const keyFactors = compactList(section.keyFactors, formatKeyFactor, 8);
    if (keyFactors) lines.push(`keyFactors:\n${keyFactors}`);
  }

  const links = compactList(section.sourceLinks, formatSourceLink, compact ? 3 : 6);
  if (links) lines.push(`sourceLinks:\n${links}`);
  return lines.filter(Boolean).join('\n');
}

function formatPositioning(positioning) {
  if (!positioning) return '';
  return `## positioning
bottomLine: ${positioning.bottomLine || ''}
investment: ${positioning.investment || ''}
career: ${positioning.career || ''}
business: ${positioning.business || ''}`;
}

function formatScores(scores) {
  if (!scores) return '';
  return `## scores
velocity: ${scores.velocity ?? ''}
antifragility: ${scores.antifragility ?? ''}
moat: ${scores.moat ?? ''}
reversibility: ${scores.reversibility ?? ''}`;
}

function formatScenarioAlternatives(alternatives, compact = false) {
  if (!Array.isArray(alternatives) || alternatives.length === 0) return '';
  const items = alternatives.slice(0, compact ? 6 : 10).map((item) => {
    if (typeof item === 'string') return `- ${item}`;
    const label = item.label || item.title || item.name || 'alternative';
    const summary = item.summary || item.description || '';
    return `- ${label}${summary ? `: ${summary}` : ''}`;
  }).join('\n');
  return `## scenarioAlternatives\n${items}`;
}

function formatHistoricalAnchor(anchor) {
  if (!anchor) return '';
  return `## historicalAnchor
paradigm: ${anchor.paradigm || ''}
connection: ${anchor.connection || ''}`;
}

function buildProbReportContext(asset, options = {}) {
  const { compact = false } = options;
  const lines = [
    '# Prob Report Shared Asset',
    `id: ${asset.id || ''}`,
    `title: ${asset.title || ''}`,
    `reportDate: ${asset.reportDate || ''}`,
    `rank: ${asset.rank ?? ''}`,
    `tags: ${Array.isArray(asset.tags) ? asset.tags.join(', ') : ''}`,
    `publishedAt: ${stringifyDate(asset.publishedAt)}`,
    '',
    ...PROB_SECTION_KEYS.map((key) => formatSection(key, asset[key], { compact })),
    formatPositioning(asset.positioning),
    formatScores(asset.scores),
    formatScenarioAlternatives(asset.scenarioAlternatives, compact),
    formatHistoricalAnchor(asset.historicalAnchor),
  ];

  if (!compact) {
    const sourceLinks = compactList(asset.sourceLinks, formatSourceLink, 12);
    if (sourceLinks) lines.push(`## sourceLinks\n${sourceLinks}`);
  } else {
    const sourceLinks = compactList(asset.sourceLinks, formatSourceLink, 6);
    if (sourceLinks) lines.push(`## sourceLinks\n${sourceLinks}`);
  }

  if (asset.groundTruth) {
    lines.push(`## groundTruth
status: ${asset.groundTruth.status || ''}
summary: ${asset.groundTruth.summary || ''}
checkedAt: ${stringifyDate(asset.groundTruth.checkedAt)}`);
  }

  return lines.filter(Boolean).join('\n\n');
}

export function formatProbReportForBuddy(asset, maxChars = PROB_REPORT_CONTEXT_LIMIT) {
  const full = buildProbReportContext(asset, { compact: false });
  if (full.length <= maxChars) return full;

  const compact = buildProbReportContext(asset, { compact: true });
  if (compact.length <= maxChars) return compact;

  return compact.slice(0, maxChars).trimEnd() + '\n\n[Prob report context truncated]';
}
